import { useEffect, useState, useCallback } from "react";
import { api, formatNumber, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Loader2, Pencil, Trash2, Filter, Activity } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const ANO_ACTION_LABELS = {
  ano_created: { label: "Ano oluşturuldu", color: "bg-sky-100 text-sky-800 border-sky-200" },
  ano_updated: { label: "Ano güncellendi", color: "bg-slate-100 text-slate-700 border-slate-200" },
  ano_deleted: { label: "Ano silindi", color: "bg-red-100 text-red-800 border-red-200" },
  stage_kazi_on: { label: "Kazı ✓", color: "bg-green-100 text-green-800 border-green-300" },
  stage_kazi_off: { label: "Kazı kaldırıldı", color: "bg-slate-100 text-slate-600 border-slate-200" },
  stage_radye_on: { label: "Radye ✓", color: "bg-green-100 text-green-800 border-green-300" },
  stage_radye_off: { label: "Radye kaldırıldı", color: "bg-slate-100 text-slate-600 border-slate-200" },
  stage_perde_on: { label: "Perde ✓", color: "bg-green-100 text-green-800 border-green-300" },
  stage_perde_off: { label: "Perde kaldırıldı", color: "bg-slate-100 text-slate-600 border-slate-200" },
  stage_tabliye_on: { label: "Tabliye ✓", color: "bg-green-100 text-green-800 border-green-300" },
  stage_tabliye_off: { label: "Tabliye kaldırıldı", color: "bg-slate-100 text-slate-600 border-slate-200" },
};

function AnoActivities() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dere/anos/activities", { params: { limit: 100 } });
        setRows(data);
      } catch (err) {
        console.error("Ano aktiviteleri yüklenemedi:", err);
      }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="site-card rounded-lg overflow-hidden" data-testid="ano-activities">
      <div className="p-4 border-b border-slate-200 flex items-center gap-2">
        <Activity size={16} className="text-sky-500" />
        <h3 className="text-sm font-black uppercase tracking-widest text-slate-700">Dere / Ano Aktivite Geçmişi (Son {rows.length})</h3>
      </div>
      {loading ? (
        <div className="py-8 flex justify-center"><Loader2 className="animate-spin text-sky-500" size={22} /></div>
      ) : rows.length === 0 ? (
        <div className="py-8 text-center text-sm text-slate-500">Henüz ano üzerinde işlem yapılmamış.</div>
      ) : (
        <div className="max-h-72 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 sticky top-0">
              <tr className="text-xs uppercase tracking-widest text-slate-500">
                <th className="text-left py-2 px-4 font-bold">Zaman</th>
                <th className="text-left py-2 px-4 font-bold">Ano</th>
                <th className="text-left py-2 px-4 font-bold">İşlem</th>
                <th className="text-left py-2 px-4 font-bold">Detay</th>
                <th className="text-left py-2 px-4 font-bold">Kullanıcı</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const cfg = ANO_ACTION_LABELS[r.action] || { label: r.action, color: "bg-slate-100 text-slate-700" };
                const ts = r.timestamp ? new Date(r.timestamp).toLocaleString("tr-TR") : "";
                return (
                  <tr key={r.id} className="border-t border-slate-100" data-testid={`activity-${r.id}`}>
                    <td className="py-2 px-4 text-xs text-slate-500 font-mono-num whitespace-nowrap">{ts}</td>
                    <td className="py-2 px-4 font-semibold text-slate-900">Ano #{r.ano_number} <span className="text-xs text-slate-500">({r.group_key})</span></td>
                    <td className="py-2 px-4"><span className={`inline-block text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border ${cfg.color}`}>{cfg.label}</span></td>
                    <td className="py-2 px-4 text-slate-600 text-xs">{r.details || "—"}</td>
                    <td className="py-2 px-4 text-slate-500 text-xs">{r.actor_name}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function HistoryPage() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterItem, setFilterItem] = useState("all");
  const [filterSub, setFilterSub] = useState("all");
  const [filterGroup, setFilterGroup] = useState("all");
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cat, ent] = await Promise.all([
        api.get("/items/catalog"),
        api.get("/entries", { params: { limit: 1000 } }),
      ]);
      setCatalog(cat.data);
      setEntries(ent.data);
    } catch {
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = entries.filter((e) => {
    if (filterItem !== "all" && e.item_key !== filterItem) return false;
    if (filterSub !== "all" && e.sub_item_key !== filterSub) return false;
    if (filterGroup !== "all" && (e.group_key || "") !== filterGroup) return false;
    return true;
  });

  const itemMap = Object.fromEntries(catalog.map((c) => [c.key, c]));

  const canEdit = (e) => user?.role === "admin" || (user?.role === "editor" && e.created_by === user.id);

  const filterItemObj = catalog.find((c) => c.key === filterItem);

  const onDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/entries/${deleteId}`);
      toast.success("Kayıt silindi");
      setDeleteId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="history-page">
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-sky-500 mb-2">Kayıt Geçmişi</div>
        <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight font-display">İşlem Geçmişi</h1>
        <p className="text-slate-600 mt-2">Girilen tüm günlük metrajları ve dere/ano aktivitelerini görüntüleyin.</p>
      </div>

      <AnoActivities />

      {/* Filters */}
      <div className="site-card p-4 rounded-lg flex flex-wrap items-end gap-4" data-testid="history-filters">
        <div className="flex items-center gap-2 text-slate-700 font-semibold text-sm">
          <Filter size={16} /> Filtreler:
        </div>
        <div className="flex-1 min-w-[180px]">
          <Label className="text-xs font-semibold text-slate-600">İmalat</Label>
          <Select value={filterItem} onValueChange={(v) => { setFilterItem(v); setFilterSub("all"); setFilterGroup("all"); }}>
            <SelectTrigger className="border-2 border-slate-300 h-11 mt-1" data-testid="filter-item">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü</SelectItem>
              {catalog.map((c) => (
                <SelectItem key={c.key} value={c.key}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[180px]">
          <Label className="text-xs font-semibold text-slate-600">Alt Kırılım</Label>
          <Select value={filterSub} onValueChange={setFilterSub} disabled={filterItem === "all"}>
            <SelectTrigger className="border-2 border-slate-300 h-11 mt-1" data-testid="filter-sub">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü</SelectItem>
              {filterItemObj?.sub_items.map((s) => (
                <SelectItem key={s.key} value={s.key}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {filterItemObj && (filterItemObj.groups || []).length > 0 && (
          <div className="flex-1 min-w-[180px]">
            <Label className="text-xs font-semibold text-slate-600">Grup</Label>
            <Select value={filterGroup} onValueChange={setFilterGroup}>
              <SelectTrigger className="border-2 border-slate-300 h-11 mt-1" data-testid="filter-group">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                {filterItemObj.groups.map((g) => (
                  <SelectItem key={g.key} value={g.key}>{g.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="text-sm text-slate-600 pl-2">
          <span className="font-mono-num font-bold text-slate-900">{filtered.length}</span> kayıt
        </div>
      </div>

      {/* Table */}
      <div className="site-card rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="animate-spin text-sky-500" size={32} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-24 text-center text-slate-500" data-testid="history-empty">
            Bu filtreye uygun kayıt bulunamadı.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-xs uppercase tracking-widest text-slate-500">
                  <th className="text-left py-3 px-4 font-bold">Tarih</th>
                  <th className="text-left py-3 px-4 font-bold">İmalat</th>
                  <th className="text-left py-3 px-4 font-bold">Grup</th>
                  <th className="text-left py-3 px-4 font-bold">Alt Kırılım</th>
                  <th className="text-right py-3 px-4 font-bold">Miktar</th>
                  <th className="text-left py-3 px-4 font-bold">Lokasyon</th>
                  <th className="text-left py-3 px-4 font-bold">Not</th>
                  <th className="text-left py-3 px-4 font-bold">Kaydeden</th>
                  <th className="text-right py-3 px-4 font-bold">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => {
                  const it = itemMap[e.item_key];
                  const sub = it?.sub_items.find((s) => s.key === e.sub_item_key);
                  const grp = e.group_key ? (it?.groups || []).find((g) => g.key === e.group_key) : null;
                  return (
                    <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`history-row-${e.id}`}>
                      <td className="py-3 px-4 font-mono-num text-slate-900">{e.date}</td>
                      <td className="py-3 px-4 font-semibold text-slate-900">{it?.name}</td>
                      <td className="py-3 px-4 text-sky-700 font-semibold">{grp?.name || "—"}</td>
                      <td className="py-3 px-4 text-slate-700">{sub?.name}</td>
                      <td className="py-3 px-4 text-right font-mono-num font-bold text-sky-600">
                        {formatNumber(e.quantity)} <span className="text-slate-500 font-normal">{sub?.unit}</span>
                      </td>
                      <td className="py-3 px-4 text-slate-600">{e.location || "—"}</td>
                      <td className="py-3 px-4 text-slate-600 max-w-[220px] truncate">{e.note || "—"}</td>
                      <td className="py-3 px-4 text-slate-600 text-xs">{e.created_by_name}</td>
                      <td className="py-3 px-4 text-right">
                        {canEdit(e) ? (
                          <div className="flex items-center justify-end gap-1">
                            <Button size="sm" variant="ghost" onClick={() => setEditing(e)} data-testid={`edit-btn-${e.id}`}>
                              <Pencil size={14} />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setDeleteId(e.id)} data-testid={`delete-btn-${e.id}`}>
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <EditDialog
        entry={editing}
        catalog={catalog}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />

      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kaydı sil?</AlertDialogTitle>
            <AlertDialogDescription>
              Bu metraj kaydını kalıcı olarak silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">İptal</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={onDelete} data-testid="delete-confirm">
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EditDialog({ entry, catalog, onClose, onSaved }) {
  const [itemKey, setItemKey] = useState("");
  const [subItemKey, setSubItemKey] = useState("");
  const [groupKey, setGroupKey] = useState("");
  const [date, setDate] = useState("");
  const [quantity, setQuantity] = useState("");
  const [location, setLocation] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (entry) {
      setItemKey(entry.item_key);
      setSubItemKey(entry.sub_item_key);
      setGroupKey(entry.group_key || "");
      setDate(entry.date);
      setQuantity(String(entry.quantity));
      setLocation(entry.location || "");
      setNote(entry.note || "");
    }
  }, [entry]);

  if (!entry) return null;

  const currentItem = catalog.find((c) => c.key === itemKey);
  const currentSub = currentItem?.sub_items.find((s) => s.key === subItemKey);
  const itemGroups = currentItem?.groups || [];
  const hasGroups = itemGroups.length > 0;

  const onSave = async () => {
    if (!itemKey || !subItemKey || !quantity) {
      toast.error("İmalat, alt kırılım ve miktar zorunludur");
      return;
    }
    if (hasGroups && !groupKey) {
      toast.error("Bu imalat için grup seçmelisiniz");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        item_key: itemKey,
        sub_item_key: subItemKey,
        date,
        quantity: parseFloat(quantity),
        location,
        note,
        group_key: hasGroups ? groupKey : null,
      };
      await api.put(`/entries/${entry.id}`, payload);
      toast.success("Kayıt güncellendi");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!entry} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg" data-testid="edit-dialog">
        <DialogHeader>
          <DialogTitle>Kaydı Düzenle</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold">İmalat</Label>
              <Select value={itemKey} onValueChange={(v) => { setItemKey(v); setSubItemKey(""); setGroupKey(""); }}>
                <SelectTrigger className="border-2 border-slate-300 mt-1 h-11" data-testid="edit-item-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {catalog.map((c) => (<SelectItem key={c.key} value={c.key}>{c.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold">Alt Kırılım</Label>
              <Select value={subItemKey} onValueChange={setSubItemKey} disabled={!currentItem}>
                <SelectTrigger className="border-2 border-slate-300 mt-1 h-11" data-testid="edit-sub-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {currentItem?.sub_items.map((s) => (<SelectItem key={s.key} value={s.key}>{s.name} ({s.unit})</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {hasGroups && (
            <div>
              <Label className="text-xs font-semibold">Grup</Label>
              <Select value={groupKey} onValueChange={setGroupKey}>
                <SelectTrigger className="border-2 border-slate-300 mt-1 h-11" data-testid="edit-group-select"><SelectValue placeholder="Grup seçin" /></SelectTrigger>
                <SelectContent>
                  {itemGroups.map((g) => (<SelectItem key={g.key} value={g.key}>{g.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold">Tarih</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="border-2 border-slate-300 mt-1 h-11" data-testid="edit-date" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Miktar {currentSub && <span className="text-sky-600">({currentSub.unit})</span>}</Label>
              <Input type="number" step="0.01" value={quantity} onChange={(e) => setQuantity(e.target.value)} className="border-2 border-slate-300 mt-1 h-11 font-mono-num" data-testid="edit-quantity" />
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold">Lokasyon</Label>
            <Input value={location} onChange={(e) => setLocation(e.target.value)} className="border-2 border-slate-300 mt-1 h-11" data-testid="edit-location" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Not</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} className="border-2 border-slate-300 mt-1" data-testid="edit-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="edit-cancel">İptal</Button>
          <Button className="bg-sky-500 hover:bg-sky-600 text-white font-bold" onClick={onSave} disabled={saving} data-testid="edit-save">
            {saving ? <Loader2 className="animate-spin" size={16} /> : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
